import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    // Dashboard data
    statistics: {
        totalQueries: 0,
        successfulQueries: 0,
        failedQueries: 0,
        successRate: 0,
        failureRate: 0,
        averageResponseTime: 0,
        totalQueriesTrend: 0,
        successRateTrend: 0,
        responseTimeTrend: 0,
        periodLabel: 'Last 24 Hours',
    },

    // Volume data for charts
    volumeData: [],

    // Type distribution
    typeDistribution: [],

    // System performance
    systemPerformance: 0,
    systemHealthStatus: 'Excellent',

    // Query history
    queryHistory: [],
    queryHistoryMeta: {
        total: 0,
        page: 1,
        pageSize: 10,
        totalPages: 0,
    },

    // Filters
    timeRange: 'Last24Hours',
    startDate: null,
    endDate: null,
    unitId: null,
    deviceType: null,
    status: null,
    queryType: null,
    minResponseTime: null,
    maxResponseTime: null,
    page: 1,
    pageSize: 10,
    sortColumn: null,
    sortDirection: null,

    // Alert configurations
    alerts: [],

    // Loading states
    dashboardLoading: false,
    historyLoading: false,
    alertsLoading: false,

    // Error states
    error: null,
    statusMessage: null,
};

const queryMonitoringSlice = createSlice({
    name: 'queryMonitoring',
    initialState,
    reducers: {
        // Fetch dashboard data
        fetchDashboard: (state) => {
            state.dashboardLoading = true;
            state.error = null;
        },
        fetchDashboardSuccess: (state, action) => {
            state.dashboardLoading = false;
            state.statistics = action.payload.statistics || initialState.statistics;
            state.volumeData = action.payload.volumeData || [];
            state.typeDistribution = action.payload.typeDistribution || [];
            state.systemPerformance = action.payload.systemPerformance || 0;
            state.systemHealthStatus = action.payload.systemHealthStatus || 'Excellent';
            state.error = null;
        },
        fetchDashboardFailure: (state, action) => {
            state.dashboardLoading = false;
            state.error = action.payload;
        },

        // Fetch query history
        fetchQueryHistory: (state) => {
            state.historyLoading = true;
            state.error = null;
        },
        fetchQueryHistorySuccess: (state, action) => {
            state.historyLoading = false;
            state.queryHistory = action.payload.data || [];
            state.queryHistoryMeta = action.payload.meta || initialState.queryHistoryMeta;
            state.error = null;
        },
        fetchQueryHistoryFailure: (state, action) => {
            state.historyLoading = false;
            state.error = action.payload;
        },

        // Fetch alerts
        fetchAlerts: (state) => {
            state.alertsLoading = true;
            state.error = null;
        },
        fetchAlertsSuccess: (state, action) => {
            state.alertsLoading = false;
            state.alerts = action.payload || [];
            state.error = null;
        },
        fetchAlertsFailure: (state, action) => {
            state.alertsLoading = false;
            state.error = action.payload;
        },

        // Configure alert
        configureAlert: (state) => {
            state.alertsLoading = true;
            state.statusMessage = null;
        },
        configureAlertSuccess: (state, action) => {
            state.alertsLoading = false;
            state.statusMessage = { type: 'success', text: 'Alert configured successfully' };
        },
        configureAlertFailure: (state, action) => {
            state.alertsLoading = false;
            state.statusMessage = { type: 'error', text: action.payload };
        },

        // Update alert
        updateAlert: (state) => {
            state.alertsLoading = true;
            state.statusMessage = null;
        },
        updateAlertSuccess: (state, action) => {
            state.alertsLoading = false;
            state.statusMessage = { type: 'success', text: 'Alert updated successfully' };
        },
        updateAlertFailure: (state, action) => {
            state.alertsLoading = false;
            state.statusMessage = { type: 'error', text: action.payload };
        },

        // Delete alert
        deleteAlert: (state) => {
            state.alertsLoading = true;
            state.statusMessage = null;
        },
        deleteAlertSuccess: (state, action) => {
            state.alertsLoading = false;
            state.statusMessage = { type: 'success', text: 'Alert deleted successfully' };
        },
        deleteAlertFailure: (state, action) => {
            state.alertsLoading = false;
            state.statusMessage = { type: 'error', text: action.payload };
        },

        // Export reports
        exportReport: (state) => {
            state.statusMessage = { type: 'info', text: 'Exporting report...' };
        },
        exportReportSuccess: (state) => {
            state.statusMessage = { type: 'success', text: 'Report exported successfully' };
        },
        exportReportFailure: (state, action) => {
            state.statusMessage = { type: 'error', text: action.payload };
        },

        // Update filters
        setTimeRange: (state, action) => {
            state.timeRange = action.payload;
            state.page = 1; // Reset to first page
        },
        setDateRange: (state, action) => {
            state.startDate = action.payload.startDate;
            state.endDate = action.payload.endDate;
            state.timeRange = 'CustomRange';
            state.page = 1;
        },
        setUnitFilter: (state, action) => {
            state.unitId = action.payload;
            state.page = 1;
        },
        setDeviceTypeFilter: (state, action) => {
            state.deviceType = action.payload;
            state.page = 1;
        },
        setStatusFilter: (state, action) => {
            state.status = action.payload;
            state.page = 1;
        },
        setQueryTypeFilter: (state, action) => {
            state.queryType = action.payload;
            state.page = 1;
        },
        setResponseTimeRange: (state, action) => {
            state.minResponseTime = action.payload.min;
            state.maxResponseTime = action.payload.max;
            state.page = 1;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setPageSize: (state, action) => {
            state.pageSize = action.payload;
            state.page = 1;
        },
        setSorting: (state, action) => {
            state.sortColumn = action.payload.column;
            state.sortDirection = action.payload.direction;
        },

        // Clear filters
        clearFilters: (state) => {
            state.timeRange = 'Last24Hours';
            state.startDate = null;
            state.endDate = null;
            state.unitId = null;
            state.deviceType = null;
            state.status = null;
            state.queryType = null;
            state.minResponseTime = null;
            state.maxResponseTime = null;
            state.page = 1;
        },

        // Clear messages
        clearStatusMessage: (state) => {
            state.statusMessage = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
});

export const {
    fetchDashboard,
    fetchDashboardSuccess,
    fetchDashboardFailure,
    fetchQueryHistory,
    fetchQueryHistorySuccess,
    fetchQueryHistoryFailure,
    fetchAlerts,
    fetchAlertsSuccess,
    fetchAlertsFailure,
    configureAlert,
    configureAlertSuccess,
    configureAlertFailure,
    updateAlert,
    updateAlertSuccess,
    updateAlertFailure,
    deleteAlert,
    deleteAlertSuccess,
    deleteAlertFailure,
    exportReport,
    exportReportSuccess,
    exportReportFailure,
    setTimeRange,
    setDateRange,
    setUnitFilter,
    setDeviceTypeFilter,
    setStatusFilter,
    setQueryTypeFilter,
    setResponseTimeRange,
    setPage,
    setPageSize,
    setSorting,
    clearFilters,
    clearStatusMessage,
    clearError,
} = queryMonitoringSlice.actions;

// Selectors
export const selectStatistics = (state) => state.queryMonitoring?.statistics || initialState.statistics;
export const selectVolumeData = (state) => state.queryMonitoring?.volumeData || [];
export const selectTypeDistribution = (state) => state.queryMonitoring?.typeDistribution || [];
export const selectSystemPerformance = (state) => state.queryMonitoring?.systemPerformance || 0;
export const selectSystemHealthStatus = (state) => state.queryMonitoring?.systemHealthStatus || 'Excellent';
export const selectQueryHistory = (state) => state.queryMonitoring?.queryHistory || [];
export const selectQueryHistoryMeta = (state) => state.queryMonitoring?.queryHistoryMeta || initialState.queryHistoryMeta;
export const selectAlerts = (state) => state.queryMonitoring?.alerts || [];
export const selectFilters = (state) => ({
    timeRange: state.queryMonitoring?.timeRange || 'Last24Hours',
    startDate: state.queryMonitoring?.startDate,
    endDate: state.queryMonitoring?.endDate,
    unitId: state.queryMonitoring?.unitId,
    deviceType: state.queryMonitoring?.deviceType,
    status: state.queryMonitoring?.status,
    queryType: state.queryMonitoring?.queryType,
    minResponseTime: state.queryMonitoring?.minResponseTime,
    maxResponseTime: state.queryMonitoring?.maxResponseTime,
    page: state.queryMonitoring?.page || 1,
    pageSize: state.queryMonitoring?.pageSize || 10,
    sortColumn: state.queryMonitoring?.sortColumn,
    sortDirection: state.queryMonitoring?.sortDirection,
});
export const selectDashboardLoading = (state) => state.queryMonitoring?.dashboardLoading || false;
export const selectHistoryLoading = (state) => state.queryMonitoring?.historyLoading || false;
export const selectAlertsLoading = (state) => state.queryMonitoring?.alertsLoading || false;
export const selectError = (state) => state.queryMonitoring?.error;
export const selectStatusMessage = (state) => state.queryMonitoring?.statusMessage;

export default queryMonitoringSlice.reducer;
